import { createContext, ReactNode, useContext, useRef, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import { EmbryoDto } from '@/services/embryo-service';

import { EmbryoAppContextType, EmbryoFilters } from '../util/types';

const EmbryoAppContext = createContext<EmbryoAppContextType | undefined>(
  undefined,
);

export const EmbryoAppProvider = ({ children }: { children: ReactNode }) => {
  const [searchParams] = useSearchParams();
  const [selectedEmbryos, setSelectedEmbryos] = useState<EmbryoDto[]>([]);
  const [embryoData, setEmbryoData] = useState<EmbryoDto[] | null>(null);
  const [filters, setFilters] = useState<EmbryoFilters>(() => ({
    stateCode: 0,
    ownerId: searchParams.get('ownerId') ?? undefined,
    searchTerm: searchParams.get('search') ?? undefined,
  }));
  const [isLoading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const selectedEmbryosRef = useRef<EmbryoDto[]>([]);

  const wrappedSetSelectedEmbryos = (embryos: EmbryoDto[]) => {
    selectedEmbryosRef.current = embryos;
    setSelectedEmbryos(embryos);
  };

  const updateFilter = (
    key: keyof EmbryoFilters,
    value: EmbryoFilters[keyof EmbryoFilters],
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({});
    setEmbryoData([]);
  };

  const resetState = () => {
    setSelectedEmbryos([]);
    selectedEmbryosRef.current = [];
    setEmbryoData(null);
    setFilters({});
    setLoading(false);
    setError(null);
  };

  const contextValue: EmbryoAppContextType = {
    selectedEmbryos,
    selectedEmbryosRef,
    embryoData,
    filters,
    isLoading,
    error,
    setSelectedEmbryos: wrappedSetSelectedEmbryos,
    setEmbryoData,
    setFilters,
    updateFilter,
    setLoading,
    setError,
    clearFilters,
    resetState,
  };

  return (
    <EmbryoAppContext.Provider value={contextValue}>
      {children}
    </EmbryoAppContext.Provider>
  );
};

export const useEmbryoAppContext = () => {
  const context = useContext(EmbryoAppContext);
  if (context === undefined) {
    throw new Error('useEmbryoApp must be used within an EmbryoAppProvider');
  }
  return context;
};
